/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { PrinterFinderListItem } from '@gh/builder-shared';
import { FormStateSnapshot, getFormStateSnapshotValue, PlainImmutablePageState } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { emptySelectionModel, isSelectionModelEmpty, SelectionModel } from '@gh/core-data';
import { FormMode } from '@gh/core-ui';
import { isAllTrue, isNotNil } from '@gh/core-util';
import { combineLatest$ } from '@gh/rx';
import { map$, switchMap$ } from '@gh/rx/operators';
import { isNil, negate } from 'lodash';
import { ScheduleSupervisorsReportFilterDialogConfig } from '../../resources/schedule-list-business.service';
import {
  SchedulesSupervisorPrintMode,
  SchedulesSupervisorReportDetails,
  schedulesSupervisorReportDetailsInit,
  SchedulesSupervisorReportStep,
  ScheduleSupervisorReportFilter,
  validateSchedulesSupervisorTaskFilter,
} from '../../resources/schedule-list.model';

@Injectable()
export class ScheduleSupervisorsReportFilterFormState extends PlainImmutablePageState<SchedulesSupervisorReportDetails> {
  taskFilter$ = this.select(({ taskFilter }) => taskFilter);
  printerName$ = this.select(({ printerName }) => printerName);
  printer$ = this.select(({ printer }) => printer);
  printers$ = this.select(({ printers }) => printers);
  foundPrinters$ = this.printers$.pipe(switchMap$(() =>
    this.select(({ printers, printerName }) =>
      printers.filter((printer) =>
        printer.cloudPrinterName.toLowerCase().includes((printerName || '').toLowerCase())))));
  step$ = this.select(({ step }) => step);
  isTaskStep$ = this.step$.pipe(map$((step) => step === SchedulesSupervisorReportStep.Task));
  isSupplierStep$ = this.step$.pipe(map$((step) => step === SchedulesSupervisorReportStep.Supplier));
  isPrinterStep$ = this.step$.pipe(map$((step) => step === SchedulesSupervisorReportStep.Printer));
  printMode$ = this.select(({ printMode }) => printMode);
  isPrintEmailAttachments$ = this.select(({ isPrintEmailAttachments }) => isPrintEmailAttachments);
  taskSelectionModel$ = this.select(({ taskSelectionModel }) => taskSelectionModel);
  isTaskExist$ = this.select(({ taskExists }) => taskExists);
  isTaskSelectionModelEmpty$ = this.taskSelectionModel$.pipe(map$(negate(isSelectionModelEmpty)));
  canPreviewTasks$ = combineLatest$(this.isTaskSelectionModelEmpty$, this.isTaskExist$).pipe(map$(isAllTrue));
  canOpenSupplierStep$ = combineLatest$(
    this.taskFilter$.pipe(
      map$(getFormStateSnapshotValue),
      map$((taskFilter) => isNil(validateSchedulesSupervisorTaskFilter(taskFilter)))),
    this.isTaskExist$).pipe(
    map$(isAllTrue));
  supplierSelectionModel$ = this.select(({ supplierSelectionModel }) => supplierSelectionModel);
  canOpenFinalStep$ = this.supplierSelectionModel$.pipe(map$(negate(isSelectionModelEmpty)));
  canFinish$ = this.select(({ printer }) => isNotNil(printer));
  isEmailIncluded$ = this.select(({ printMode }) =>
    printMode === SchedulesSupervisorPrintMode.Both || printMode === SchedulesSupervisorPrintMode.Emailed);

  constructor() {
    super(schedulesSupervisorReportDetailsInit(false), FormMode.EditMode);
  }

  get taskFilter(): FormStateSnapshot<ScheduleSupervisorReportFilter> {
    return this.value.taskFilter;
  }

  get taskSelectionModel(): SelectionModel {
    return this.value.taskSelectionModel;
  }

  get supplierSelectionModel(): SelectionModel {
    return this.value.supplierSelectionModel;
  }

  get printMode(): SchedulesSupervisorPrintMode {
    return this.value.printMode;
  }

  get isTaskFilterValid(): boolean {
    return isNil(validateSchedulesSupervisorTaskFilter(getFormStateSnapshotValue(this.taskFilter)));
  }

  get printerId(): Maybe<ID> {
    const { printer } = this.value;
    return printer ? printer.cloudPrinterId : void 0;
  }

  build({ isIncludeUnsentOrders }: ScheduleSupervisorsReportFilterDialogConfig): void {
    this.setValue(schedulesSupervisorReportDetailsInit(isIncludeUnsentOrders));
  }

  setTaskFilter(taskFilter: FormStateSnapshot<ScheduleSupervisorReportFilter>): void {
    this.setField('taskFilter', taskFilter);
  }

  setPrintMode(printMode: SchedulesSupervisorPrintMode): void {
    this.setField('printMode', printMode);
  }

  setPrinter(printer: PrinterFinderListItem): void {
    this.setField('printer', printer);
  }

  setPrinterName(name: string): void {
    this.setField('printerName', name);
  }

  setPrintEmailAttachments(isPrintEmailAttachments: boolean): void {
    this.setField('isPrintEmailAttachments', isPrintEmailAttachments);
  }

  setTaskSelectionModel(selectionModel: SelectionModel): void {
    this.setField('taskSelectionModel', selectionModel);
  }

  setTaskExists(exists: boolean): void {
    this.setField('taskExists', exists);
  }

  setSupplierSelectionModel(selectionModel: SelectionModel): void {
    this.setField('supplierSelectionModel', selectionModel);
  }

  setPrinters(printers: PrinterFinderListItem[]): void {
    this.setField('printers', printers);
  }

  resetTaskSelection(): void {
    this.setField('taskSelectionModel', emptySelectionModel());
    this.setField('taskExists', false);
  }

  resetSupplierSelection(): void {
    this.setField('supplierSelectionModel', emptySelectionModel());
  }

  goBack(): void {
    if (this.value.step === SchedulesSupervisorReportStep.Supplier) {
      this.setField('step', SchedulesSupervisorReportStep.Task);
    } else if (this.value.step === SchedulesSupervisorReportStep.Printer) {
      this.setField('step', SchedulesSupervisorReportStep.Supplier);
    }
  }

  goNext(): void {
    if (this.value.step === SchedulesSupervisorReportStep.Task) {
      this.setField('step', SchedulesSupervisorReportStep.Supplier);
    } else if (this.value.step === SchedulesSupervisorReportStep.Supplier) {
      this.setField('step', SchedulesSupervisorReportStep.Printer);
    }
  }
}
